/**
 * Notifications système (navigateur / OS) quand l'onglet n'est pas au premier plan.
 * Utilisé par le dashboard pour alerter l'agent d'une nouvelle notification.
 *
 * Bannière et son : dépendent des paramètres Windows (Paramètres > Système > Notifications)
 * et du navigateur (Chrome : paramètres > Confidentialité > Paramètres des sites > Notifications).
 * On utilise les options pour maximiser les chances (requireInteraction, renotify, silent: false).
 *
 * Coordination multi-onglets : un seul onglet affiche la notification (BroadcastChannel).
 */

const SYS_NOTIF_CHANNEL = 'crispclone-sys-notif';
const TAB_ID_KEY = 'crispclone_tab_id';
const ELECTION_MS = 120;

type IntentMessage = { type: 'intent_to_show'; notificationId: string; timestamp: number; tabId: string };

function getTabId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch {
    return `tab_${Date.now()}`;
  }
}

/**
 * Détermine si cet onglet doit afficher la notification système pour l'id donné.
 * Tous les onglets ouverts s'envoient leur "intention" via BroadcastChannel ;
 * un seul est élu (timestamp le plus ancien) pour éviter N notifications pour 1 message.
 */
export function shouldShowSystemNotificationInThisTab(notificationId: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      resolve(true);
      return;
    }
    const tabId = getTabId();
    const timestamp = Date.now();
    const channel = new BroadcastChannel(SYS_NOTIF_CHANNEL);
    const intents: IntentMessage[] = [{ type: 'intent_to_show', notificationId, timestamp, tabId }];

    channel.postMessage({ type: 'intent_to_show', notificationId, timestamp, tabId } as IntentMessage);

    const onMessage = (e: MessageEvent<IntentMessage>) => {
      if (e.data?.type === 'intent_to_show' && e.data.notificationId === notificationId) {
        intents.push(e.data);
      }
    };
    channel.addEventListener('message', onMessage);

    setTimeout(() => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      intents.sort((a, b) => a.timestamp - b.timestamp || String(a.tabId).localeCompare(String(b.tabId)));
      const winner = intents[0];
      resolve(winner.tabId === tabId);
    }, ELECTION_MS);
  });
}

/**
 * Demande la permission d'afficher des notifications si pas encore accordée.
 * @returns true si les notifications sont autorisées (ou déjà accordées)
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const perm = await Notification.requestPermission();
  return perm === 'granted';
}

/** Icône par défaut pour la notification (favicon du site). */
function getDefaultIcon(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin + '/favicon.ico';
}

/** Événement personnalisé émis au clic sur une notification quand une conversation est associée. */
export const NOTIFICATION_OPEN_CONVERSATION_EVENT = 'crispclone-open-conversation';

export interface NotificationOpenConversationDetail {
  conversationId: string;
}

/** Événement personnalisé émis au clic sur une notification côté widget visiteur (ouvrir le chat). */
export const NOTIFICATION_OPEN_WIDGET_EVENT = 'crispclone-open-widget';

/**
 * Affiche une notification système si la page n'est pas visible et que la permission est accordée.
 * Au clic : focalise la fenêtre, et selon le contexte : ouvre la conversation (dashboard, `conversationId`)
 * ou le widget de chat (visiteur, `openWidget`).
 *
 * Options utilisées pour favoriser bannière + son (selon support navigateur/OS) :
 * - silent: false → respecte le son du système
 * - renotify: true → à chaque nouvelle notif (même tag) on re-notifie (nouvelle bannière/son)
 * (requireInteraction non utilisé : peut afficher un bouton fermer plus visible sous Windows)
 */
export async function showSystemNotification(
  title: string,
  body: string,
  options?: { tag?: string; icon?: string; conversationId?: string; openWidget?: boolean }
): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (!document.hidden) return;

  const granted = await requestNotificationPermission();
  if (!granted) return;

  const tag = options?.tag ?? `crispclone-notification-${Date.now()}`;
  const conversationId = options?.conversationId;
  const openWidget = options?.openWidget ?? false;
  const opts: NotificationOptions & { renotify?: boolean } = {
    body,
    tag,
    icon: options?.icon ?? getDefaultIcon(),
    silent: false,
    renotify: true,
  };
  const notification = new Notification(title, opts);

  notification.onclick = () => {
    window.focus();
    notification.close();
    if (conversationId) {
      window.dispatchEvent(
        new CustomEvent<NotificationOpenConversationDetail>(NOTIFICATION_OPEN_CONVERSATION_EVENT, {
          detail: { conversationId },
        })
      );
    }
    if (openWidget) {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_WIDGET_EVENT));
    }
  };
}
